import { getTranslation } from 'src/utilities/translator';
import { ObjectFieldValueProps } from './types';

export const EXCEL_TEMPLATE_PATH =
  'https://oy-static-assets.s3.ap-southeast-1.amazonaws.com/pay/Instruction-for-VA-Creation_2024.xlsx';
export const FILE_UPLOAD_ACCEPT =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';
export const MAX_TIMESTAMP = 8640000000000000;
export const MB = 10 ** 6;
export const MAX_FILE_SIZE = 10 * MB;

export const NUM_DATA_IN_REQ = 10;
export const NUM_PARALLEL_REQ = 5;
export const BATCH_REQ_TIMEOUT_BETWEEN_ATTEMPTS = 1000;
export const BATCH_FAILED_REQ_TIMEOUT_BETWEEN_ATTEMPTS = 1000;
export const NUM_OF_BATCH_FAILED_REQ_RETRY_ATTEMPTS = 3;

export const FILE_HEADER_LENGTH = 13;
export const MIN_AMOUNT = 10000;
export const MAX_AMOUNT = 1000000000000;
export const DEFAULT_PAGE_SIZE = 4;

export const DefaultFormat = {
  date: 'dd MMM yyyy, HH:mm',
  time: 'HH:mm',
};

export const Label = {
  columns: {
    amount: 'Amount',
    bankName: 'Bank Name',
    edited: 'Edited?',
    email: 'Email',
    full_name: 'Full Name',
    no: 'No.',
    partnerTxId: 'Partner Transaction ID',
    partnerUserId: 'Partner User ID',
    txExpiryDateTime: 'Transaction Expiration Date & Time',
    usage: 'Usage',
    usageLimit: 'Usage Limit',
    vaName: 'VA Name',
    vaType: 'VA Type',
    vaExpirationType: 'VA Expiration Type',
    vaExpiryDateTime: 'VA Expiration Date & Time',
  },
  columnDesc: {
    email: 'Optional',
    partnerTxId: 'Optional',
    txExpiryDateTime: "Money can't be received after transaction expired",
    usageLimit: 'Type -1 for unlimited usage',
    vaExpiryDateTime: "VA can't be used after VA expired",
  },
  modal: {
    cancel: 'Cancel',
    close: 'Close',
    deactivateDesc:
      'Your customer can’t use the virtual account number anymore',
    deactivate: 'Deactivate',
    successTitle: 'Successfully Create',
    successDesc: 'You can download new VA details by clicking the button below',
    validate: 'Validate',
    ignoreWarning: 'Ignore Warning',
    noInvalidInput: 'No Invalid Input',
    allInputUpdated: 'All Invalid Input Updated',
  },
  toast: {
    emptyRow: 'Unable to load data, empty rows.',
    invalidFileFormat: 'Unable to load data, invalid file format',
    successfullyValidated: 'Data has been successfully validated',
    newFileUploaded: 'A new file is successfully uploaded',
  },
  rowData: {
    open: 'open',
    closed: 'closed',
    singleUse: 'single use',
    multipleUse: 'multiple use',
    lifetime: 'lifetime',
    nonLifetime: 'non lifetime',
  },
  loader: {
    validationProcessed: 'Processing the data',
    submissionProcessed: 'Submission is being processed',
    calculatingLimitCreationVa: 'Calculating the limit creation VA for today',
    uploadDataProcessed: 'Uploading data is being processed',
  },
  create: {
    submitError: 'Error while creating va for row',
  },
  key: {
    openVa: 'open_va',
    rowId: 'row_id',
    success: 'success',
  },
  addManually: 'Add Virtual Account Detail Manually',
  addNewRow: 'Add New Row',
  changeUploadedFile: 'Change Excel',
  download: 'Download',
  edit: 'Edit',
  excel: 'Excel',
  uploadFile: 'Drag & Drop to upload Excel file here',
  submit: 'Submit',
  template: "Don't have the Virtual Account Excel Template yet?",
  totalVirtualAccount: 'Total Virtual Account',
  validate: 'Validate',
};

export enum CreateVaTableFieldName {
  Amount = 'amount',
  BankName = 'bank_code',
  Checked = 'checked',
  Email = 'email',
  FullName = 'full_name',
  Edited = 'edited',
  No = 'no',
  PartnerTxId = 'partner_trx_id',
  PartnerUserId = 'partner_user_id',
  TxExpiryDateTime = 'trx_expiration_time',
  Usage = 'is_single_use',
  UsageLimit = 'trx_counter',
  VaName = 'username_display',
  VaType = 'is_open',
  VaExpirationType = 'is_lifetime',
  VaExpiryDateTime = 'expiration_time',
}

export const CreateVaTableFieldPlaceholder: ObjectFieldValueProps = {
  [CreateVaTableFieldName.PartnerUserId]: getTranslation(
    'common_typeTheVAUserID',
  ),
  [CreateVaTableFieldName.PartnerTxId]: getTranslation(
    'common_typeTransactionId',
  ),
  [CreateVaTableFieldName.BankName]: getTranslation('common_selectABank'),
  [CreateVaTableFieldName.VaName]: getTranslation('common_typeAVAName'),
  [CreateVaTableFieldName.VaType]: getTranslation('common_chooseOne'),
  [CreateVaTableFieldName.Usage]: getTranslation('common_chooseOne'),
  [CreateVaTableFieldName.UsageLimit]: getTranslation('common_typeANumber'),
  [CreateVaTableFieldName.VaExpirationType]: getTranslation('common_chooseOne'),
  [CreateVaTableFieldName.VaExpiryDateTime]: 'dd/mm/yyyy HH:mm',
  [CreateVaTableFieldName.TxExpiryDateTime]: 'dd/mm/yyyy HH:mm',
  [CreateVaTableFieldName.Email]: getTranslation('common_typeAnEmail'),
  [CreateVaTableFieldName.FullName]: getTranslation('common_typeFullName'),
};

export const CreateVaTableFieldError = {
  required: {
    [CreateVaTableFieldName.PartnerUserId]: 'Partner User ID is required',
    [CreateVaTableFieldName.BankName]: 'Bank Name is required',
    [CreateVaTableFieldName.Amount]: 'Amount is required for Closed VA',
  },
  invalid: {
    min: {
      [CreateVaTableFieldName.Amount]: 'Min. Amount is Rp 10.000',
    },
    max: {
      [CreateVaTableFieldName.Amount]: 'Max. Amount is Rp 1.000.000.000.000',
    },
    syntaxError: {
      [CreateVaTableFieldName.PartnerUserId]:
        'Partner user id contains a word or syntax that is not allowed.',
      [CreateVaTableFieldName.PartnerTxId]:
        'Partner transaction id contains a word or syntax that is not allowed.',
      [CreateVaTableFieldName.FullName]:
        'Full name contains a word or syntax that is not allowed.',
      [CreateVaTableFieldName.VaName]:
        'VA name contains a word or syntax that is not allowed.',
    },
    [CreateVaTableFieldName.UsageLimit]: 'Invalid Usage Limit format',
    [CreateVaTableFieldName.Email]: 'Invalid Email format',
    [CreateVaTableFieldName.TxExpiryDateTime]:
      'Transaction Expiration Date can not exceed VA Expiration Date',
  },
  condition: {
    [CreateVaTableFieldName.UsageLimit]: ' Min. Usage Limit format is -1',
    [CreateVaTableFieldName.TxExpiryDateTime]:
      'Tx Expiration Date & Time must be before or the same with VA Expiration Date & Time',
  },
  notFound: {
    [CreateVaTableFieldName.BankName]: 'Bank Name not found',
  },
  notUnique: {
    [CreateVaTableFieldName.PartnerUserId]: 'Partner User ID must be unique',
    [CreateVaTableFieldName.PartnerTxId]:
      'Partner Transaction ID must be unique',
  },
};

export const CreateVaTableFieldWarning: ObjectFieldValueProps = {
  [CreateVaTableFieldName.VaType]: 'Will be filled with `Open VA` if blank',
  [CreateVaTableFieldName.Usage]: 'Will be filled with `Multiple Use` if blank',
  [CreateVaTableFieldName.UsageLimit]: 'Will be set to unlimited if blank',
  [CreateVaTableFieldName.VaExpirationType]:
    'Will be filled with `Non Lifetime` if blank',
  [CreateVaTableFieldName.VaExpiryDateTime]:
    'Will be filled with 24 hours after creation date & time if blank',
  [CreateVaTableFieldName.TxExpiryDateTime]:
    'Will be filled with VA Expiration Date & Time if blank',
};

export const PROGRESS_LABEL = [
  {
    label: getTranslation('bulkDisbursement_progressLabel_uploadFillData'),
  },
  {
    label: getTranslation('bulkDisbursement_progressLabel_validateData'),
  },
  {
    label: getTranslation('common_status_finish'),
    subLabel: getTranslation('createVa_progresLabelFinishSubLabel'),
  },
];

export const UsageOptions = [
  {
    label: 'Single Use',
    value: 'single use',
  },
  {
    label: 'Multiple Use',
    value: 'multiple use',
  },
];

export const VaTypeOptions = [
  {
    label: 'Open',
    value: 'open',
  },
  {
    label: 'Closed',
    value: 'closed',
  },
];

export const VaExpirationTypeOptions = [
  {
    label: 'Lifetime',
    value: 'lifetime',
  },
  {
    label: 'Non Lifetime',
    value: 'non lifetime',
  },
];
